import { ReactNode } from "react";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { Module } from "__generated__/gql/graphql";
import LabeledValue from "components/Shared/LabeledValue";
import { CopyableBrandEmail } from "./BrandEmail";

const fragment = gql(/* GraphQL */ `
  fragment BrandEmailsFields on Brand {
    id
    slug
    brandModulesList(orderBy: MODULE_ASC) {
      id
      module
    }
  }
`);

enum BrandEmailAlias {
  Data,
  Orders,
}

const ALIASES = [BrandEmailAlias.Data, BrandEmailAlias.Orders] as const;

const SUFFIX_BY_ALIAS: Record<BrandEmailAlias, string> = {
  [BrandEmailAlias.Data]: "data",
  [BrandEmailAlias.Orders]: "orders",
};

const LABELS_BY_ALIAS: Record<BrandEmailAlias, string> = {
  [BrandEmailAlias.Data]: "Data",
  [BrandEmailAlias.Orders]: "Purchase Orders",
};

const HELP_BY_ALIAS: Record<BrandEmailAlias, ReactNode> = {
  [BrandEmailAlias.Data]: (
    <>
      Used for emailing brand data and reports from distributors and retailers
      to be used in analytics.
    </>
  ),
  [BrandEmailAlias.Orders]: (
    <>
      Used for emailing purchase orders received from distributors to be tracked
      in the Purchase Orders module.
    </>
  ),
};

function getIsAliasShown(
  alias: BrandEmailAlias,
  brand: DocumentType<typeof fragment> | null | undefined,
) {
  if (!brand) return false;

  if (alias === BrandEmailAlias.Data) {
    return brand.brandModulesList?.some((m) => m.module === Module.ANALYTICS);
  }

  if (alias === BrandEmailAlias.Orders) {
    return brand.brandModulesList?.some(
      (m) => m.module === Module.PURCHASE_ORDERS,
    );
  }
}

type Props = {
  brand: FragmentType<typeof fragment> | null | undefined;
};

export default function BrandEmails({ brand: brandFragment }: Props) {
  const brand = getFragmentData(fragment, brandFragment);

  const aliases = ALIASES.filter((alias) => getIsAliasShown(alias, brand));

  return (
    <>
      <LabeledValue
        label="General"
        value={<CopyableBrandEmail slug={brand?.slug} isLinked isTruncated />}
        valueProps={{ fontWeight: 500 }}
        helpText={
          <>
            Used for sharing general brand content
            {aliases.length > 0 ? (
              <>
                {" "}
                <em>that does not have a dedicated email address below</em>
              </>
            ) : (
              ""
            )}
            .
          </>
        }
      />

      {aliases.map((alias) => (
        <LabeledValue
          key={alias}
          label={LABELS_BY_ALIAS[alias]}
          helpText={HELP_BY_ALIAS[alias]}
          valueProps={{ fontWeight: 500 }}
          value={
            <CopyableBrandEmail
              slug={brand?.slug}
              plusSuffix={SUFFIX_BY_ALIAS[alias]}
              isLinked
              isTruncated
            />
          }
        />
      ))}
    </>
  );
}
